/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */

/* eslint-disable jsx-a11y/label-has-associated-control */
import { Field, useFormikContext } from "formik";
import { FC, KeyboardEventHandler, useCallback } from "react";

import getValueByStringPath from "web/utils/page/product/domesticTourism/getValueByStringPath";
import {
  getNextSelectIndex,
  isSelectCloseKey,
  isSelectCommitKey,
  isSelectNavKey,
} from "web/utils/ui/selectKeyboard";

import classes from "./selectItem.scss";

interface ISelectItemProps {
  option:
    | string
    | {
        value: string;
        label: string;
        id?: string | undefined;
      };
  closeAction: (event?: React.MouseEvent | React.KeyboardEvent) => void;
  name: string;
  isOpen: boolean;
  optionIdPrefix: string;
  index: number;
  count: number;
  activeIndex: number;
  onRequestActiveIndexChange: (nextIndex: number) => void;
  onRequestClose: () => void;
}

const SelectItem: FC<ISelectItemProps> = ({
  option,
  closeAction,
  name,
  isOpen,
  optionIdPrefix,
  index,
  count,
  activeIndex,
  onRequestActiveIndexChange,
  onRequestClose,
}) => {
  const { values, setFieldValue } =
    useFormikContext<Record<string, string | number>>();
  const currentValue = getValueByStringPath(values, name);
  const { value, label } =
    typeof option === "string"
      ? { value: option, label: option }
      : (option as { value: string; label: string });
  const isActive = currentValue && currentValue === value;

  const clickHandler = useCallback(
    (event: React.MouseEvent<HTMLLabelElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setFieldValue(name, value);
      closeAction(event);
    },
    [closeAction, name, setFieldValue, value]
  );

  const keyDownHandler: KeyboardEventHandler<HTMLLabelElement> = useCallback(
    (event) => {
      const { key } = event;

      if (isSelectNavKey(key)) {
        event.preventDefault();
        event.stopPropagation();
        const nextIndex =
          getNextSelectIndex({
            key,
            currentIndex: index,
            count,
          }) ?? index;
        onRequestActiveIndexChange(nextIndex);
        return;
      }

      if (isSelectCommitKey(key)) {
        event.preventDefault();
        event.stopPropagation();
        setFieldValue(name, value);
        closeAction(event);
        return;
      }

      if (isSelectCloseKey(key)) {
        event.preventDefault();
        event.stopPropagation();
        onRequestClose();
      }
    },
    [
      closeAction,
      count,
      index,
      name,
      onRequestActiveIndexChange,
      onRequestClose,
      setFieldValue,
      value,
    ]
  );

  const classNameRootActive = isActive && classes.rootActive;
  const classNameRoot = classNameRootActive || classes.root;

  return (
    <label
      key={value}
      className={classNameRoot}
      onClick={clickHandler}
      onKeyDown={keyDownHandler}
      tabIndex={isOpen ? 0 : -1}
      role="option"
      aria-selected={!!isActive}
      id={`${optionIdPrefix}-option-${value}`}
      data-active={activeIndex === index ? "true" : "false"}
    >
      <Field
        type="radio"
        className="visually-hidden"
        name={name}
        value={value}
        dataT1="select_item_radio"
        tabIndex={-1}
      />
      <span>{label}</span>
    </label>
  );
};

export default SelectItem;
